import React, { FormEventHandler } from 'react';
import { Heading } from '@mos-cat/typography';
import { t, Trans } from '@lingui/macro';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import Select from '@mos-front-components/public/es/components/MosSelect';
import { Organization } from '../../../helpers/initialState';
import TextField from '../../shared/TextField/TextField';
import Message, { THEME } from '@mos-cat/message';
import { List, Paragraph } from '@mos-cat/typography';
import Checkbox from '@mos-cat/checkbox';
import { getValue } from '../../../constants/getFieldValues';
import { Field } from '../../../declarations/widget-ums';
import renderOrganizationSuggestion from './helpers/renderOrganizationSuggestion';
import checkForDisabledValue from './helpers/checkValue';
import { ORGANIZATION_FIELD_NAME } from '../../../constants/fieldNames';
import { i18n } from '../../../helpers/i18n';
import { getPayload } from '../../../helpers/handlers';
import { SPECIAL_VALUE } from '../../../helpers/address';

interface Props {
  education: Organization;
  isStudying: Field<string>;
  onStudyChange: FormEventHandler;

  onEducationChange(name, value): void;

  onInputChange(value, method, event): void;

  setFromSuggest(value): void;

  onBlur(payload: boolean): void;
  onEducationBlur: FormEventHandler;
}

function Education({
  education,
  isStudying,
  onStudyChange,
  onEducationChange,
  onEducationBlur,
  onInputChange,
  setFromSuggest,
  onBlur,
}: Props): JSX.Element {
  const { isFoundInCatalog, institutionName, suggestions } = education;
  const handleBlur = (event, { highlightedSuggestion }) => {
    onBlur(!suggestions.includes(highlightedSuggestion));
  };

  const institutionNameValue = getValue(institutionName);
  const placeholderForFullValue = i18n._(
    t`Наименование городского образовательного учреждения`
  );

  const placeholderForEmptyValueForSelect = i18n._(
    t`Выбор городской образовательной организации`
  );
  const placeholderForEmptyValueForInput = i18n._(
    t`Наименование городской образовательной организации`
  );
  const placeholder = institutionNameValue.trim().length
    ? placeholderForFullValue
    : placeholderForEmptyValueForSelect;

  const placeholderForInput = institutionNameValue.trim().length
    ? placeholderForFullValue
    : placeholderForEmptyValueForInput;

  const isNotStudying = typeof isStudying === 'boolean' && !isStudying;
  const disableSelect = isNotStudying || !isFoundInCatalog;
  const selectValue = disableSelect ? '' : institutionNameValue;
  const placeholderForSelect = disableSelect
    ? placeholderForEmptyValueForSelect
    : placeholder;

  const { Item } = List;

  return (
    <div className="Education StepContentBlock">
      <Heading level={3} className="PguFormGrid__row">
        <Trans>Сведения об обучении ребёнка</Trans>
      </Heading>

      <Message theme={THEME.INFO} className="educationInfoMessage">
        <Paragraph className="educationInfoMessage__text">
          В Москве с 1 января 2025 года в соответствии с указом Президента
          Российской Федерации от 23.01.2024 № 63 «О мерах социальной поддержки
          многодетных семей» действуют новые правила присвоения льготного
          статуса многодетной семьи для тех, кто получает статус впервые.
        </Paragraph>
        <List className="educationInfoMessage__list">
          <Item>
            Если вы обращаетесь за присвоением льготного статуса впервые –
            укажите сведения об обучении ребенка в возрасте от 18 до 23 лет, в
            остальных случаях выберите «Ребенок не обучается» (информацию об
            обучении детей младше 18 лет указывать не требуется).
          </Item>

          <Item>
            Если льготный статус уже был присвоен ранее – укажите сведения об
            обучении ребенка в возрасте от 16 до 18 лет, в остальных случаях
            выберите «Ребенок не обучается» (информацию об обучении детей младше
            16 лет и старше 18 лет указывать не требуется).
          </Item>
        </List>
      </Message>

      <div className="PguFormGrid__row">
        <Checkbox
          name="isStudying"
          checked={isNotStudying}
          label={i18n._(t`Ребёнок не обучается`)}
          onChange={onStudyChange}
          value="isStudying"
        />
      </div>
      <div className="PguFormGrid__row">
        <Select
          className="PguFormGrid_col ym-hide-content"
          name={ORGANIZATION_FIELD_NAME.institutionName}
          placeholder={placeholderForSelect}
          suggestions={suggestions}
          value={selectValue}
          hasError={institutionName.error}
          onChange={setFromSuggest}
          onInputChange={onInputChange}
          renderSuggestion={renderOrganizationSuggestion}
          disabled={disableSelect}
          onBlur={handleBlur}
          withClear
        />
      </div>
      <div className="PguFormGrid__row">
        <Checkbox
          name={ORGANIZATION_FIELD_NAME.isFoundInCatalog}
          checked={!isFoundInCatalog}
          label={i18n._(t`Организации нет в списке`)}
          onChange={({ target: { name, checked } }) =>
            onEducationChange(name, !checked)
          }
          value={ORGANIZATION_FIELD_NAME.isFoundInCatalog}
          disabled={isNotStudying}
        />
      </div>
      {!isFoundInCatalog && (
        <div className="PguFormGrid__row">
          <TextField
            className="PguFormGrid_col"
            placeholder={placeholderForInput}
            inputProps={{
              name: ORGANIZATION_FIELD_NAME.institutionName,
              ...institutionName,
              onChange: ({ target: { name, value } }) =>
                onEducationChange(name, { value }),
              onBlur: onEducationBlur,
              maxLength: 500,
            }}
          />
        </div>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onStudyChange({ target: { name, checked } }) {
    dispatch({
      type: 'child/isStudying/change',
      payload: { [name]: !checked },
    });
  },
  onEducationChange(name, value) {
    dispatch({
      type: 'child/education/change',
      payload: { [name]: value },
    });
  },
  onInputChange(value, method) {
    if (['click', 'enter'].includes(method)) {
      return;
    }
    if (['up', 'down'].includes(method)) {
      dispatch({
        type: `child/education/set`,
        payload: value,
      });
      return;
    }
    if (!method) {
      dispatch({
        type: 'child/education/change',
        payload: { [ORGANIZATION_FIELD_NAME.institutionName]: { value: '' } },
      });
      return;
    }
    dispatch({
      type: 'child/education/change',
      payload: { [ORGANIZATION_FIELD_NAME.institutionName]: { value } },
    });
  },
  setFromSuggest(suggest): void {
    const { value, text } = suggest;
    const isDisableValue = checkForDisabledValue(value);
    if (!text || isDisableValue) return;

    if (SPECIAL_VALUE[value] === SPECIAL_VALUE[SPECIAL_VALUE.retry]) {
      dispatch({
        type: 'child/education/retry',
      });
      return;
    }
    if (SPECIAL_VALUE[value] === SPECIAL_VALUE[SPECIAL_VALUE.switchToManual]) {
      dispatch({
        type: 'child/education/change',
        payload: { [ORGANIZATION_FIELD_NAME.isFoundInCatalog]: false },
      });
      return;
    }

    dispatch({ type: 'child/education/set', payload: text });
  },
  onBlur(payload) {
    dispatch({ type: `child/education/institutionName/setError`, payload });
  },
  onEducationBlur(event) {
    dispatch({
      type: 'child/education/change',
      payload: getPayload(event, true),
    });
  },
});

export default connect(
  ({ child: { education, isStudying } }) => ({ education, isStudying }),
  mapDispatchToProps
)(Education);
